import React, { useState } from 'react';
import { Wallet, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { accountAPI } from '../services/api';

// Admin order drawer: refund the order as store credit in one click
const RefundToWalletPanel = ({ order, onDone }) => {
  const paid = Number(order.paid_amount ?? order.total ?? 0) + Number(order.wallet_used || 0);
  const [amount, setAmount] = useState(paid.toFixed(2));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (order.wallet_refund) {
    return (
      <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="order-wallet-refunded">
        <CheckCircle size={16} /> Remboursée en avoir : <strong>{Number(order.wallet_refund.amount).toFixed(2)} €</strong> le {new Date(order.wallet_refund.at).toLocaleDateString('fr-FR')} ({order.wallet_refund.author})
      </p>
    );
  }
  if (order.refund) return null;
  if (!['En attente', 'En cours', 'Annulée', 'Remboursée'].includes(order.status)) return null;

  const refund = async () => {
    const v = parseFloat(String(amount).replace(',', '.'));
    if (!v || v <= 0) { toast.error('Montant invalide'); return; }
    if (!window.confirm(`Créditer ${v.toFixed(2)} € sur le compte de dépôt du client et passer la commande en « Remboursée » ?`)) return;
    setBusy(true);
    try {
      const r = await accountAPI.refundToWallet(order.id, v);
      toast.success(`${r.message} — solde client ${r.balance_after.toFixed(2)} €${r.restocked_qty ? ` · stock restitué (+${r.restocked_qty})` : ''}`);
      onDone && onDone(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remboursement impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-dashed border-[#1e3a5f]/40 rounded-lg p-3 bg-blue-50/40" data-testid="order-refund-panel">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="order-refund-open" className="text-sm font-medium text-[#1e3a5f] inline-flex items-center gap-2 hover:underline"><Wallet size={16} /> Rembourser en avoir (compte de dépôt)</button>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Montant de l'avoir (€) — réglé : {paid.toFixed(2)} €</label>
            <Input data-testid="order-refund-amount" type="number" min="0.01" step="0.01" max={paid} value={amount} onChange={(e) => setAmount(e.target.value)} className="w-36" />
          </div>
          <p className="text-xs text-gray-500 pb-2 flex-1 min-w-[200px]">Libellé automatique « Remboursement commande {order.order_number} ». La commande passe en « Remboursée », le stock est restitué et le client reçoit un e-mail (simulé).</p>
          <Button onClick={refund} disabled={busy} data-testid="order-refund-confirm" className="bg-[#1e3a5f] hover:bg-[#2d4a6f]">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Wallet size={16} className="mr-2" />} Créditer l'avoir</Button>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="order-refund-cancel">Annuler</Button>
        </div>
      )}
    </div>
  );
};

export default RefundToWalletPanel;
